import {withHandlers} from 'recompose'

import {skipToken} from '@reduxjs/toolkit/query'
import compose from 'lodash/flowRight'
import * as React from 'react'
import {connect} from 'react-redux'
import type {EntryPointProps} from 'react-relay'
import {usePreloadedQuery} from 'react-relay'

import {toggleOverview as toggleOverviewAction} from '../../../../../actions/overview'
import {getParams} from '../../../../../containers/EntityLink'
import withHook from '../../../../../hocs/withHook'
import {useAppSelector} from '../../../../../hooks/react-redux'
import {withActivatedByUrlItem} from '../../../../../hooks/routes'
import type {State} from '../../../../../reducers/types'
import {getHrefWithQueryParams, withLocation} from '../../../../../routes'
import {getExtensionEndpoint, getStatuses} from '../../../../../selectors'
import {getProjectsAndOverviewReady} from '../../../../../selectors/projectTrees'
import {restApi} from '../../../../../services/rest'
import type {ProjectsTreeItem} from '../../../../../types/projectTrees'
import {fetchProjectsForAllFederationServers} from '../../../../common/SidebarFooter/SidebarFooter.actions'

import type {ProjectsSidebarQuery} from './__generated__/ProjectsSidebarQuery.graphql'
import ProjectsSidebar from './ProjectsSidebar'
import {
  toggleExpandAllProjects,
  toggleExpandState as toggleExpandStateAction,
} from './ProjectsSidebar.actions'
import {query} from './ProjectsSidebar.queries'
import type {ActionProps, OwnProps, ReduxOwnProps, StateProps} from './ProjectsSidebar.types'
import {getBaseHref} from './ProjectsSidebar.utils'

const mapStateToProps = (state: State): StateProps => ({
  statuses: getStatuses(state),
  projectsAndOverviewReady: getProjectsAndOverviewReady(state),
})

const actionCreators: ActionProps = {
  toggleExpandState: toggleExpandStateAction,
  toggleExpandAll: toggleExpandAllProjects,
  toggleOverview: toggleOverviewAction,
  reloadProjects: fetchProjectsForAllFederationServers,
}

const ProjectsSidebarContainer = compose(
  withLocation,
  withActivatedByUrlItem,
  connect(mapStateToProps, actionCreators),
  React.memo,
  withHook(({projectsAndOverviewReady}: StateProps) => {
    const expandStateEndpoint = useAppSelector(
      state => getExtensionEndpoint(state, 'expandState')?.endpoint,
    )
    const {overviewExpandStateReady} = restApi.endpoints.getExpandState.useQuery(
      expandStateEndpoint ?? skipToken,
      {selectFromResult: ({data}) => ({overviewExpandStateReady: data != null})},
    )
    return {ready: projectsAndOverviewReady && overviewExpandStateReady}
  }),
  withHandlers({
    changeActiveItem:
      ({navigate, location}: ReduxOwnProps & StateProps & ActionProps) =>
      (item: ProjectsTreeItem) => {
        navigate(getHrefWithQueryParams(location, getBaseHref(item), getParams))
      },
  }),
)(ProjectsSidebar)

type Queries = {main: ProjectsSidebarQuery}

export default function ProjectsSidebarOuterContainer({
  queries,
  props,
}: EntryPointProps<Queries, {}, OwnProps, {}>) {
  const {federationServers} = usePreloadedQuery(query, queries.main)
  return <ProjectsSidebarContainer {...props} federationServersKey={federationServers} />
}
export type ProjectsSidebarOuterContainerComponent = typeof ProjectsSidebarOuterContainer
